<script lang="ts">
	import { getLuxAiController } from "../LuxAIController.svelte"
	import { Responsive } from "responsive"
	import { Models as M } from "@lux-ai/api-interface"
	import { LoadingSection } from "misc-ui"
	import LuxStudioIcon from "../icons/LuxStudioIcon.svelte"
	import SubscribeCard from "./SubscribeCard.svelte"
	import ActiveGenerations from "./active-generations/ActiveGenerations.svelte"
	import ThemeCollection from "./themes/ThemeCollection.svelte"
	import { ErrorIndicator } from "luxedo-comps"
	import ThemeList from "./themes/ThemeList.svelte"

	const controller = getLuxAiController()
	const loadingPromise = controller.loadInitialData()

	let collections = $derived(controller.availableCollections)
	let activeGenerations = $derived(controller.activeGenerations)

	let canAccessPrivateThemes = $derived(
		controller.userDataGetter.getCurrentUserData().isAISuperUser,
	)

	let isUserSubscribed = $derived(controller.userDataGetter.getCurrentUserData().isSubscribed)

	let freeThemes = $derived(controller.freeThemes)
	let privateThemes = $derived(controller.availableThemes.filter((t) => !t.public))

	function filterThemesByCollection(collection: M.Collection, themes: Array<M.Theme>) {
		return themes.filter((t) => t.category === collection.name)
	}
</script>

<div id="lux-ai-gallery" class:mobile={Responsive.isMobile}>
	<div class="heading">
		<div class="icon-wrapper">
			<LuxStudioIcon strokeColor="var(--color-text-light)" />
		</div>

		<p class="info">
			Experience personalized visuals as our advanced algorithms transform curated themes to
			perfectly fit your unique projection space.
		</p>
	</div>
	{#await loadingPromise}
		<LoadingSection textContent="Loading themes" />
	{:then}
		{#if activeGenerations.length}
			<ActiveGenerations {activeGenerations} />
		{/if}

		{#if !isUserSubscribed && !canAccessPrivateThemes}
			<ThemeCollection
				themes={freeThemes}
				title={"Free Themes"}
				description={`Welcome to LuxStudio! You can get started with these free themes to see how LuxStudio can completely transform your home in minutes. Click "Generate" on any theme to get started!`}
				{activeGenerations}
			/>

			<SubscribeCard onSubscribe={controller.quickActions.jumpToSubscribe} />
		{/if}

		<ThemeList {controller} />
	{:catch}
		<ErrorIndicator
			errorMessage="Failed to load LuxStudio, please refresh and try again."
			linkToSupport
		/>
	{/await}
</div>

<style>
	#lux-ai-gallery {
		overflow-y: scroll;
		padding: 0 0.5rem;
		flex-grow: 1;
		display: flex;
		flex-direction: column;
	}

	#lux-ai-gallery :global(.Loading) {
		flex-grow: 1;
	}

	.heading {
		align-self: center;
	}

	.info {
		color: var(--color-text);
	}

	.icon-wrapper {
		max-height: 4rem;
		position: relative;
		display: flex;
		flex-direction: column;
	}

	.icon-wrapper :global(svg) {
		height: fit-content;
		width: fit-content;
		max-width: 100%;
	}

	#lux-ai-gallery :global(.collection-title) {
		margin-top: 1rem;
		margin-bottom: 0;
		font-size: var(--text-h4);
		color: var(--color-main);
	}

	#lux-ai-gallery :global(.theme-collection) {
		margin: 0 0 1rem 0;
	}

	#lux-ai-gallery :global(.themes) {
		container-type: inline-size;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;
		gap: 1rem;

		padding: 0 0.5rem;
	}

	#lux-ai-gallery :global(.section-info) {
		margin-top: 0px;
		line-height: 1.4em;
		color: var(--color-text);
	}

	#lux-ai-gallery.mobile :global(.collection-title) {
		margin-top: 1rem;
		text-align: center;
	}

	#lux-ai-gallery.mobile :global(.heading) {
		text-align: center;
	}

	#lux-ai-gallery.mobile :global(.section-info) {
		text-align: center;
	}
</style>
